import { convertCanvasRubric, convertD2LRubric, normalizeRubricScoringMode } from 'utils/rubricScoring';

export { QuizQuestionMode, QuizRubricType } from 'utils/quizConstants';

export const getImportedQuizRubric = (quiz) => {
    const assignment = quiz?.assignment ?? quiz?.api_data ?? quiz;
    const rubric = quiz?.rubric ?? assignment?.rubric;
    if (rubric?.criteria?.length) return normalizeRubricScoringMode({ ...assignment, rubric });
    if (Array.isArray(rubric) && rubric.length) {
        return convertCanvasRubric(rubric, assignment?.rubric_settings);
    }
    const d2lRubric = assignment?.Assessment?.Rubrics?.[0];
    return d2lRubric?.CriteriaGroups?.length ? convertD2LRubric(d2lRubric) : null;
};

export const hasQuizRubric = (rubric) => Boolean(rubric?.title?.trim() && rubric?.criteria?.length);

// Only written-response questions are mapped, in the order shown in the quiz overview.
export const getQuizCriterionMapping = (questions, rubric, mapByOrder) => {
    if (!mapByOrder || !rubric) return {};
    return Object.fromEntries(
        questions.flatMap(({ id }, index) => (rubric.criteria[index] ? [[id, [rubric.criteria[index].id]]] : []))
    );
};

export const reconcileQuizCriterionMapping = (mapping, rubric) => {
    const criterionIds = new Set((rubric?.criteria || []).map(({ id }) => id));
    return Object.fromEntries(
        Object.entries(mapping || {}).flatMap(([questionId, selectedIds]) => {
            const validIds = (selectedIds || []).filter((id) => criterionIds.has(id));
            return validIds.length ? [[questionId, validIds]] : [];
        })
    );
};

export const remapQuizCriterionMapping = (mapping, previousRubric, rubric) => {
    const idsByPreviousId = Object.fromEntries(
        (previousRubric?.criteria || []).flatMap((criterion, index) =>
            rubric?.criteria?.[index] ? [[criterion.id, rubric.criteria[index].id]] : []
        )
    );
    return Object.fromEntries(
        Object.entries(mapping || {}).flatMap(([questionId, selectedIds]) => {
            const remappedIds = (selectedIds || []).flatMap((id) => (idsByPreviousId[id] ? [idsByPreviousId[id]] : []));
            return remappedIds.length ? [[questionId, remappedIds]] : [];
        })
    );
};

export const reconcileQuizFeedbackTemplates = (templates, rubric) => {
    if (!templates || !rubric) return null;
    return Object.fromEntries(
        rubric.criteria.map((criterion) => [
            criterion.id,
            Object.fromEntries(
                criterion.options
                    .filter(({ id }) => templates[criterion.id]?.[id] !== undefined)
                    .map(({ id }) => [id, templates[criterion.id][id]])
            ),
        ])
    );
};

// LMS sync assigns new IDs to the criteria and ratings we send in rubric order.
export const remapQuizFeedbackTemplates = (templates, previousRubric, rubric) => {
    if (!templates || !previousRubric || !rubric) return null;
    return Object.fromEntries(
        rubric.criteria.map((criterion, index) => {
            const previousCriterion = previousRubric.criteria[index];
            return [
                criterion.id,
                Object.fromEntries(
                    criterion.options.flatMap((option, optionIndex) => {
                        const previousOption = previousCriterion?.options[optionIndex];
                        const feedback = templates[previousCriterion?.id]?.[previousOption?.id];
                        return feedback === undefined ? [] : [[option.id, feedback]];
                    })
                ),
            ];
        })
    );
};
